<template>
  <component :is="route.meta.layout" v-if="initialized">
    <router-view :key="route.fullPath"></router-view>
  </component>
</template>

<script lang="ts" setup>
import { styles as typescaleStyles } from '@material/web/typography/md-typescale-styles.js';

import { useRouter, useRoute } from 'vue-router';
import { DEFAULT_LOCALE, useI18n } from '@/composables/useI18n';
import { onBeforeMount, ref } from 'vue';
import { useRefreshToken } from '@/composables/useRefreshToken';
import { UserResponseDto } from '@/model/generated';
import { LanguageLocale } from '@/model/language-locale';

const { switchLocale } = useI18n();

const router = useRouter();
const route = useRoute();
const initialized = ref(false);

useRefreshToken();

const adjustViewport = () => {
  const minimumWidth = 410;
  const screenWidth = window.outerWidth;
  const viewportEl = document.querySelector('meta[name="viewport"]');

  if (!viewportEl) {
    return;
  }

  if (screenWidth < minimumWidth) {
    const scale = Number((screenWidth / minimumWidth).toFixed(3));
    viewportEl.setAttribute(
      'content',
      `width=device-width, initial-scale=${scale}, user-scalable=no`
    );
  } else {
    viewportEl.setAttribute('content', 'width=device-width,initial-scale=1.0');
  }
};

onBeforeMount(async () => {
  adjustViewport();

  window.addEventListener('resize', () => {
    adjustViewport();
  });

  await router.isReady();

  let locale = route.query.locale || DEFAULT_LOCALE;

  if (Array.isArray(locale)) {
    locale = locale[0] as string;
  }

  // allow only supported locales
  if (!Object.keys(UserResponseDto.locale).includes(locale.toUpperCase())) {
    locale = DEFAULT_LOCALE;
  }

  await switchLocale(locale.toLowerCase() as LanguageLocale).then(() => {
    initialized.value = true;
  });

  document.adoptedStyleSheets.push(typescaleStyles.styleSheet as CSSStyleSheet);
});
</script>
